/* eslint-disable react/jsx-no-bind */ // Needed for dnd-kit library
import { Label } from "@/components/ui/label";
import CustomView from "@/domain/entities/customViews/CustomView";
import useManageFavoriteCustomViews from "@/hooks/customView/useManageFavoriteCustomViews";
import {
    DndContext,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AddView from "./AddView";
import SortableCustomView from "./SortableCustomView";

const MAX_FAVORITE_VIEWS = 3;

const CustomViews = () => {
    const { t } = useTranslation();

    const [favoriteViews, setFavoriteViews] = useState<CustomView[]>([]);
    const [availableViews, setAvailableViews] = useState<CustomView[]>([]);

    const {
        customViews,
        editCustomViewFavoriteStatus,
        updateFavoritesViewsOrder,
    } = useManageFavoriteCustomViews();

    useEffect(() => {
        setFavoriteViews(customViews.filter((view) => view.isFavorite));
        setAvailableViews(customViews.filter((view) => !view.isFavorite));
    }, [customViews]);

    const handleAddViewToFavorites = async (view: CustomView) => {
        setFavoriteViews([...favoriteViews, view]);
        setAvailableViews(availableViews.filter((v) => v.id !== view.id));
        const result = await editCustomViewFavoriteStatus(view, true);
        if (!result) {
            setFavoriteViews(favoriteViews.filter((v) => v.id !== view.id));
            setAvailableViews([...availableViews, view]);
        }
    };

    const handleRemoveViewFromFavorites = async (view: CustomView) => {
        setFavoriteViews(favoriteViews.filter((v) => v.id !== view.id));
        setAvailableViews([...availableViews, view]);
        const result = await editCustomViewFavoriteStatus(view, false);
        if (!result) {
            setFavoriteViews([...favoriteViews, view]);
            setAvailableViews(availableViews.filter((v) => v.id !== view.id));
        }
    };

    const handleUpdateFavoritesViewsOrder = async (views: CustomView[]) => {
        setFavoriteViews(views);
        const result = await updateFavoritesViewsOrder(views);
        if (!result) {
            setFavoriteViews(favoriteViews);
        }
    };

    function handleDragEnd(event: any) {
        const { active, over } = event;

        if (active.id !== over.id) {
            const oldIndex = favoriteViews.findIndex(
                (item) => item.id === active.id,
            );
            const newIndex = favoriteViews.findIndex(
                (item) => item.id === over.id,
            );

            const newItemsList = arrayMove(favoriteViews, oldIndex, newIndex);

            handleUpdateFavoritesViewsOrder(newItemsList);
        }
    }

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5,
            },
        }),
    );

    return (
        <div className="flex w-full flex-col gap-2 py-2">
            <Label>{t("settings.homepage.customViews.title")}</Label>
            <DndContext onDragEnd={handleDragEnd} sensors={sensors}>
                <SortableContext
                    items={favoriteViews.map((view) => ({ id: view.id! }))}
                    strategy={verticalListSortingStrategy}
                >
                    {favoriteViews.map((view) => (
                        <SortableCustomView
                            key={view.id}
                            handleRemoveViewFromFavorites={
                                handleRemoveViewFromFavorites
                            }
                            view={view}
                        />
                    ))}
                </SortableContext>
            </DndContext>
            {availableViews.length > 0 &&
                favoriteViews.length < MAX_FAVORITE_VIEWS && (
                    <AddView
                        availableViews={availableViews}
                        handleAddViewToFavorites={handleAddViewToFavorites}
                    />
                )}
        </div>
    );
};

export default CustomViews;
